import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { refineScanMiss } from '@/lib/taxRateScan';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { parseLocalISODate, formatLocalISODate } from '@/lib/dateUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EntityImage } from '@/components/ui/entity-image';
import { CustomerSelect, CustomerOption } from './CustomerSelect';
import { FileAttachments } from '@/components/ui/file-attachments';
import { useCreateARInvoiceWithLineItems, useUpdateARInvoiceWithLineItems, buildARInvoiceCreateBody, ARInvoice } from '@/hooks/useAccountsReceivable';
import { recurringPayloadSchemaByType, type RecurringTemplateWire } from '@releve/shared';
import { DangerZone } from '@/components/ui/danger-zone';
import { RecurringSection } from './recurring/RecurringSection';
import {
  recurringFormFields,
  recurringFormRefinement,
  resolveRecurringDefaults,
  type RecurringFormFields,
} from './recurring/recurringFormFields';
import { useRecurringTemplateSubmit } from './recurring/useRecurringTemplateSubmit';
import { buildRecurringPayload, hydrateARInvoiceFromPayload } from '@/lib/recurringPayload';
import { suggestTemplateName, dueDaysFromTerms, todayInOrgZone, type RecurrenceFormSchemaContext } from '@/lib/recurrence';
import { useCarbonTracking } from '@/hooks/useCarbonTracking';
import { useChartOfAccounts } from '@/hooks/useChartOfAccounts';
import { useProjects } from '@/hooks/useProjects';
import { useFunds } from '@/hooks/useFunds';
import { FundSelect } from '@/components/accounting/ui/fund-select';
import { useBusinesses } from '@/hooks/useBusinesses';
import { usePersonIndex } from '@/hooks/usePeople';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useUploadTransactionAttachments, useDeleteTransactionAttachment, useTransactionAttachments } from '@/hooks/useTransactionAttachments';
import { InvoiceLineItems } from './InvoiceLineItems';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronRight } from 'lucide-react';
import { resolveTaxTreatment, toSalesTaxRateType, type SalesTaxRateType } from '@/lib/taxCalculations';
import { captureException } from '@/lib/sentry';
import { toast } from '@/hooks/useToast';
import { isTaxRateRefusal } from '@/lib/taxRateRefusal';
import { useTranslation } from '@/hooks/useTranslation';
import { getSystemAccountDisplayName } from '@/lib/account-category-utils';
import type { FileAttachment } from '@/components/ui/file-attachments';
import { PAYMENT_TERMS_OPTIONS, calculateDueDate } from '@/lib/paymentTerms';
import { useOrganizationBusinessIdentity } from '@/hooks/useOrganizationBusinessIdentity';
import { useFullTaxSettings } from '@/hooks/useOrganizationTaxSettings';
import { useBusinessVendorIdentity } from '@/hooks/useBusinessVendorIdentity';
import { OrganizationIdentityDisplay, VendorIdentityDisplay } from './BusinessIdentityDisplay';
import { DeclaredVatBanner } from './DeclaredVatBanner';
import { useARInvoiceEditability } from '@/hooks/useARInvoiceEditability';
import { arInvoiceActionGates, arInvoiceNumberLabel, isARIssued } from '@/lib/arInvoiceLifecycle';
import { Link } from 'react-router-dom';
import { useOrgBasePath } from '@/hooks/useOrgNavigate';
import { fundDetailPath } from './funds/fundStatus';
import { counterpartyPresetFields, type DefaultCounterparty } from './defaultCounterparty';

// Tax rate can be a number (0-100) or special string values
const taxRateSchema = z.union([
  z.number().min(-1).max(100),
  z.literal('exempt'),
]);

const lineItemSchema = z.object({
  revenue_account_id: z.string().min(1, 'Revenue account is required'),
  description: z.string().min(1, 'Description is required'),
  quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
  unit_price: z.number().min(0, 'Unit price must be positive'),
  tax_rate: taxRateSchema,
  tax_rate_id: z.string().optional(),
  tax_inclusive: z.boolean().default(false),
  is_reverse_charge: z.boolean().default(false),
  exemption_reason: z.string().optional(),
  exemption_reason_other: z.string().optional(),
  /** Placeholder-rate marker for the default-rate fill (form-only, never sent). */
  tax_rate_unset: z.boolean().optional(),
  /** Scanned percentage the fill pass matches (form-only, never sent). */
  tax_rate_hint: z.number().optional(),
  /** The hint is tax ÷ subtotal — matched within ±0.01 (form-only, never sent). */
  tax_rate_hint_computed: z.boolean().optional(),
  /** The user picked in the tax picker ("No VAT" included) — clears the scan-miss block (form-only, never sent). */
  tax_rate_confirmed: z.boolean().optional(),
  /**
   * Rebill link — the expense this line bills on (hidden; set by `prefill`,
   * kept by edit hydration, dropped by clone + the recurring branch).
   */
  source_expense_id: z.string().uuid().optional(),
  /**
   * The line's service category (hidden; no picker on this form). Kept by
   * edit and clone hydration and sent back, so a save (an issued re-post
   * included) never clears it. '' = none
   * (the recurring payload hydration writes ''); sent as omitted.
   */
  service_category_id: z.string().optional(),
}).superRefine(refineScanMiss);

const formSchema = z.object({
  customer_type: z.enum(['business', 'person'], { required_error: 'Please select customer type' }),
  business_id: z.string().optional(),
  business_name: z.string().optional(), // For custom business name input
  person_id: z.string().optional(),
  // No invoice_number: a draft has none — Issue assigns it (post-on-issue,
  // 20261101120000); the form only DISPLAYS it (or "Draft").
  invoice_date: z.string().min(1, 'Invoice date is required'),
  /**
   * The supply date (`metadata.supply_date`, YYYY-MM-DD); '' = the invoice
   * date. Editable on a draft, locked once issued.
   */
  supply_date: z.string().optional(),
  due_date: z.string().min(1, 'Due date is required'),
  payment_terms: z.string().optional(),
  memo: z.string().optional(),
  reference: z.string().optional(),
  ar_account_id: z.string().min(1, 'AR account is required'),
  project_id: z.string().optional(),
  fund_id: z.string().optional(),
  // Prefilled from the selected customer, then editable per invoice. Cleared
  // means "follow the customer record" — see the field below.
  customer_address: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, 'At least one line item is required'),
  // Advanced VAT Options
  self_billing: z.boolean().default(false),
  additional_vat_notes: z.string().optional(),
  // "Make this recurring" — form-local toggle + the schedule (see RecurringSection).
  ...recurringFormFields,
}).refine((data) => {
  if (data.customer_type === 'business') {
    return (data.business_id && data.business_id.length > 0) || (data.business_name && data.business_name.trim().length > 0);
  } else {
    return data.person_id && data.person_id.length > 0;
  }
}, {
  message: 'Please select or enter a business name, or select a person',
  path: ['customer_type'],
});

type FormData = z.infer<typeof formSchema>;
type FormLineItem = FormData['line_items'][number];

/** A blank line — the create default, and the base every prefilled line spreads over. */
const BASE_LINE: FormLineItem = {
  revenue_account_id: '',
  description: '',
  quantity: 1,
  unit_price: 0,
  tax_rate: 0 as SalesTaxRateType,
  tax_inclusive: false,
  is_reverse_charge: false,
  exemption_reason: '',
  exemption_reason_other: '',
  // Placeholder 0 — InvoiceLineItems fills the org default rate.
  tax_rate_unset: true,
};

/**
 * Create-mode seed from another surface — the expenses grid's "Create invoice"
 * rebill. One line per expense: description + NET amount (the form always
 * sends `tax_inclusive: false`), quantity 1, the org default tax rate filled by
 * InvoiceLineItems, and the hidden `source_expense_id` link the save carries.
 * The user still picks the customer and each line's revenue account.
 */
export interface ARInvoicePrefill {
  project_id?: string | null;
  line_items: Array<{ description: string; unit_price: number; source_expense_id: string }>;
}

/**
 * The resolver schema — the base shape plus the context-dependent schedule
 * rules (org-today, due days, a start locked after the first run); the
 * context is read through `getContext` at validation time.
 */
const buildFormSchema = (getContext: () => RecurrenceFormSchemaContext) =>
  formSchema.superRefine(recurringFormRefinement(getContext));

interface CreateARInvoiceFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  invoiceId?: string;
  invoice?: ARInvoice;
  onSubmitReady?: (submitHandler: () => void) => void;
  /** Show the "Make this recurring" section (feature-flag gated inside). */
  allowRecurring?: boolean;
  /** Open with the recurring box already ticked (the "New recurring…" menu). */
  defaultRecurring?: boolean;
  /** Template-edit mode: hydrate from the template; saving PATCHes it in place. */
  recurringTemplate?: RecurringTemplateWire | null;
  /** Fires when the user ticks/unticks the box — hosts swap the footer label. */
  onRecurringChange?: (enabled: boolean) => void;
  /** After a recurring CREATE the form navigates to `/accounting/recurring` unless `false`. */
  navigateOnSuccess?: boolean;
  /** Template-edit mode: the DangerZone's action — the host owns the confirm + delete. */
  onRequestDelete?: () => void;
  /**
   * Clone mode ("Duplicate" / "Make recurring" from an invoice's expanded row):
   * hydrate customer, lines, accounts, tax, project, fund, memo and the
   * customer address from an EXISTING invoice, but save as a NEW one — no id,
   * today's date, the due date kept the same number of days out; the number is
   * auto-assigned on save. Attachments are not copied. Mutually exclusive
   * with `invoiceId` / `recurringTemplate`.
   */
  cloneFrom?: ARInvoice | null;
  /**
   * Create-mode prefill (see `ARInvoicePrefill`). Read once, at mount, into
   * `defaultValues`. Mutually exclusive with `invoiceId` / `cloneFrom` /
   * `recurringTemplate`; hosts do not offer recurring alongside it.
   */
  prefill?: ARInvoicePrefill | null;
  /**
   * Preset the project on a NEW invoice (the project dashboard's Add menu).
   * Seeds `defaultValues` only: ignored in edit mode, and any source that
   * carries its own project — a rebill `prefill`, a recurring template, a
   * clone — still overrides it. The user can change or clear it.
   */
  defaultProjectId?: string;
  /**
   * Preset the counterparty on a NEW invoice (a business's or person's Add
   * menu) — the `defaultProjectId` rule; see `DefaultCounterparty`.
   */
  defaultCounterparty?: DefaultCounterparty;
}

export const CreateARInvoiceForm = ({
  onSuccess,
  onCancel,
  invoiceId,
  invoice,
  onSubmitReady,
  allowRecurring = false,
  defaultRecurring = false,
  recurringTemplate = null,
  cloneFrom = null,
  prefill = null,
  onRecurringChange,
  navigateOnSuccess,
  onRequestDelete,
  defaultProjectId,
  defaultCounterparty,
}: CreateARInvoiceFormProps) => {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const orgBasePath = useOrgBasePath();
  const createARInvoice = useCreateARInvoiceWithLineItems();
  const updateARInvoice = useUpdateARInvoiceWithLineItems();
  const uploadAttachments = useUploadTransactionAttachments();
  const deleteAttachment = useDeleteTransactionAttachment();
  const isEditMode = !!invoiceId;
  const isTemplateMode = !!recurringTemplate;
  const { submit: submitRecurringTemplate } = useRecurringTemplateSubmit();

  // The ONE "today" the schedule uses — the org's calendar day (docs/DATES.md).
  const orgToday = useMemo(
    () => todayInOrgZone(selectedOrganization?.timezone),
    [selectedOrganization?.timezone],
  );

  // Template-edit mode: the stored payload is jsonb — re-validate before hydrating.
  const templatePayload = useMemo(() => {
    if (!recurringTemplate) return null;
    const parsed = recurringPayloadSchemaByType.ar_invoice.safeParse(recurringTemplate.payload);
    if (!parsed.success) {
      captureException(new Error('Recurring AR template payload failed validation'), {
        context: 'AR recurring template hydrate',
        templateId: recurringTemplate.id,
        issues: parsed.error.issues.map((i) => i.path.join('.')).join(','),
      });
      return null;
    }
    return parsed.data;
  }, [recurringTemplate]);

  // Read at validation time by the resolver (see buildFormSchema).
  const recurrenceContextRef = useRef<RecurrenceFormSchemaContext>({ orgToday, documentType: 'ar_invoice' });
  recurrenceContextRef.current = {
    orgToday,
    documentType: 'ar_invoice',
    originalStartDate: recurringTemplate?.start_date ?? null,
    startLocked: (recurringTemplate?.occurrences_generated ?? 0) > 0,
  };
  const resolverSchema = useMemo(() => buildFormSchema(() => recurrenceContextRef.current), []);
  const { data: accounts, isLoading: isLoadingAccounts } = useChartOfAccounts(selectedOrganization?.id);
  const { data: projects, isLoading: isLoadingProjects } = useProjects(selectedOrganization?.id);
  const { data: funds, isLoading: isLoadingFunds } = useFunds(selectedOrganization?.id);
  const { data: businesses, isLoading: isLoadingBusinesses } = useBusinesses(selectedOrganization?.id);
  const { data: people, isLoading: isLoadingPeople } = usePersonIndex(selectedOrganization?.id, { sortByName: true });
  // Business identity hooks (org-level — no dependency on form state)
  const { data: orgIdentity } = useOrganizationBusinessIdentity(selectedOrganization?.id);
  const { data: taxSettings } = useFullTaxSettings(selectedOrganization?.id);

  // Load existing attachments if editing: every AR attachment keys on the
  // invoice since post-on-issue (a draft has no journal); older ones sit on
  // its journal — the hook ORs the two.
  const { data: existingAttachments } = useTransactionAttachments(
    invoice?.ledger_transaction_id ?? undefined,
    undefined,
    invoice?.id,
  );

  // Read-only fallback (`arInvoiceReadOnlyReason`, the one rule the hosts
  // share): a FUND INSTALLMENT is owned by its fund (`FUND_MANAGED`), and a
  // DECLARED invoice has a VAT line on a filed return (AR has no header-only
  // save — its update re-posts every line, which the declared-VAT guard
  // refuses; docs/TAX.md "Edit guard"). The AR panel hides Edit on a declared
  // invoice, but the `invoice_number` cell click and the router-state deep
  // link still land here. Either way every input is disabled (one
  // `<fieldset disabled>` below), a banner says why, and the hosts withhold
  // submit (ARInvoicesTab, EditARInvoiceDialog). The invoice comes from
  // useARInvoiceById (AR_INVOICE_DOCUMENT_SELECT: `*` + `entries(*)`), so
  // `source`, the fund and the VAT tag are all on the wire.
  // An ISSUED invoice dated in a filed VAT period is read-only too
  // (`filed_period` — the update RPC refuses it, `VAT_PERIOD_FILED`); the fact
  // comes from `ar_invoice_index.in_filed_vat_period`, read by the hook.
  // Read-only there means FULLY read-only: the fund of such an invoice moves
  // through the panel's "Change fund" action (`update_ar_invoice_fund`, Clay
  // 2026-11-02: fund is not a VAT dimension), never through this form — the
  // form only points to it (`showChangeFundHint`).
  // The hook also hands back the source invoice's index row (edit AND clone):
  // its `ar_account_id` is the AR control account (`_ar_posting_ar_leg`).
  const sourceInvoice = isEditMode ? invoice : cloneFrom ?? undefined;
  const editability = useARInvoiceEditability(selectedOrganization?.id, sourceInvoice);
  const readOnlyReason = isEditMode ? editability.readOnlyReason : null;
  const isReadOnly = readOnlyReason !== null;
  // Declared / filed-period read-only, but the panel's "Change fund" applies
  // (the same gate as the panel: manual, issued, nothing received).
  const showChangeFundHint =
    (readOnlyReason?.kind === 'declared' || readOnlyReason?.kind === 'filed_period') &&
    !!invoice &&
    arInvoiceActionGates({
      status: invoice.status,
      source: invoice.source,
      amount_received: invoice.amount_received,
      declared: true,
    }).changeFund;
  const sourceIndexArAccountId = editability.indexRow?.ar_account_id ?? null;
  // An ISSUED invoice (post-on-issue): its date, supply date, customer,
  // currency and AR account are locked (`ISSUED_LOCKED`); the fund is not —
  // it is re-stamped in place. Amounts stay editable — re-posted in place —
  // only while nothing has been received (else `paid` above).
  const isIssued = isEditMode && !!invoice && isARIssued(invoice);
  // The fund installment's link target: its fund code, from the funds this
  // form already loads for the Fund picker.
  const readOnlyFundCode =
    readOnlyReason?.kind === 'fund'
      ? funds?.find((f) => f.id === readOnlyReason.fundId)?.fund_code ?? null
      : null;

  // Check if all data is loaded (especially important for edit mode). An
  // issued source also waits for its index row (the AR account comes from it).
  const isLoadingData =
    isLoadingAccounts || isLoadingProjects || isLoadingFunds || isLoadingBusinesses || isLoadingPeople ||
    editability.indexLoading;
  const [isFormReady, setIsFormReady] = useState(!isEditMode); // Non-edit mode is ready immediately

  // File attachments state
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);

  // Auto-generated tax notes (reverse charge, exemption reasons) — stored separately from user memo
  const taxNotesRef = useRef('');
  const [hasTaxNotes, setHasTaxNotes] = useState(false);

  // Advanced VAT Options state
  const [vatOptionsOpen, setVatOptionsOpen] = useState(false);

  // Recurring defaults go through `defaultValues` (never a mount-time setValue).
  const recurringDefaults: RecurringFormFields = resolveRecurringDefaults({
    documentType: 'ar_invoice',
    orgToday,
    template: recurringTemplate,
    defaultRecurring,
    dueDays: 30,
  });
  // Template-edit mode seeds the (disabled) date fields from the schedule so
  // the base `.min(1)` rules still pass; the payload strip drops them anyway.
  const templateStart = recurringTemplate?.start_date;
  const templateStartDate = templateStart ? parseLocalISODate(templateStart) : null;
  const templateDue =
    templateStartDate && recurringTemplate
      ? formatLocalISODate(addDays(templateStartDate, recurringTemplate.due_days ?? 0)) ?? templateStart
      : undefined;

  const counterpartyPreset = counterpartyPresetFields(defaultCounterparty, !!invoiceId);

  const form = useForm<FormData>({
    resolver: zodResolver(resolverSchema),
    defaultValues: {
      customer_type: (counterpartyPreset?.type ?? 'business') as 'business' | 'person',
      business_id: counterpartyPreset?.business_id ?? '',
      business_name: counterpartyPreset?.business_name ?? '',
      person_id: counterpartyPreset?.person_id ?? '',
      invoice_date: templateStart ?? format(new Date(), "yyyy-MM-dd"),
      supply_date: '',
      due_date: templateDue ?? calculateDueDate(new Date(), 'net_30') ?? '',
      payment_terms: 'net_30',
      memo: '',
      reference: '',
      ar_account_id: '',
      project_id: (!invoiceId && defaultProjectId) || '',
      fund_id: '',
      customer_address: '',
      line_items: [{ ...BASE_LINE }],
      // Advanced VAT Options
      self_billing: false,
      additional_vat_notes: '',
      // Rebill prefill: create mode is ready at mount, so defaultValues is
      // enough (no reset effect); InvoiceLineItems fills each line's default
      // tax rate over the `tax_rate_unset` placeholder.
      ...(prefill && prefill.line_items.length > 0
        ? {
            project_id: prefill.project_id ?? '',
            line_items: prefill.line_items.map((l) => ({
              ...BASE_LINE,
              ...l,
              quantity: 1,
              tax_rate: 0 as SalesTaxRateType,
              tax_rate_unset: true,
            })),
          }
        : {}),
      // Template-edit mode: the document half comes from the stored payload.
      ...(templatePayload ? hydrateARInvoiceFromPayload(templatePayload) : {}),
      ...recurringDefaults,
    },
  });

  // After a save, reset — but never back to a rebill prefill: its linked lines
  // are the mount-time defaults, and restoring them would let a second Save
  // bill the same expenses again if a host kept the form mounted.
  const resetAfterSave = () => {
    if (prefill) {
      form.reset({ ...form.formState.defaultValues, project_id: '', line_items: [{ ...BASE_LINE }] });
    } else {
      form.reset();
    }
  };

  const customerType = form.watch('customer_type');
  // While ticked, the invoice's dates are set by the schedule (fields disabled,
  // values kept so the base schema passes; the payload strip drops them).
  const recurringEnabled = form.watch('recurring_enabled');
  const selectedArAccountId = form.watch('ar_account_id');
  const selectedBusinessId = form.watch('business_id');
  const selectedPersonId = form.watch('person_id');

  // Carbon tracking: watch line items so stored carbon remaps when the form
  // populates on edit, and per-line carbon (the picker) attaches to each
  // revenue account. An AR invoice is a DRAFT until it is issued (it books
  // nothing), so carbon keys on the INVOICE id; Issue graduates the rows onto
  // the posting. An issued invoice's rows may carry the journal id (older ones
  // carry only that), so both keys are read and written.
  const watchedLineItems = form.watch('line_items');
  const carbonLineItemAccountIds = useMemo(
    () =>
      (watchedLineItems ?? []).map(
        (li: { revenue_account_id?: string }) => li?.revenue_account_id || undefined,
      ),
    [watchedLineItems],
  );
  const {
    carbonDataByLineItem,
    updateCarbonData,
    saveCarbonEmissions: saveCarbonResults,
  } = useCarbonTracking({
    organizationId: selectedOrganization?.id ?? '',
    transactionId: invoice?.ledger_transaction_id ?? undefined,
    arInvoiceId: invoice?.id,
    lineItemAccountIds: carbonLineItemAccountIds,
  });

  // Vendor identity hook (depends on form state above)
  const { data: customerVendorIdentity } = useBusinessVendorIdentity(
    customerType === 'business' ? selectedBusinessId : undefined
  );

  // Build combined customer list from all businesses + all people
  const customerOptions: CustomerOption[] = useMemo(() => {
    const bizOptions: CustomerOption[] = (businesses || []).map(b => ({
      id: b.id,
      type: 'business' as const,
      name: b.name,
      address: b.primary_address || b.address,
      email: b.contact_email,
    }));

    const personOptions: CustomerOption[] = (people || []).map(p => ({
      id: p.id,
      type: 'person' as const,
      name: p.full_name || `${p.first_name} ${p.last_name}`,
      address: p.primary_address,
      email: p.email,
    }));

    return [...bizOptions, ...personOptions];
  }, [businesses, people]);

  // Derive selected customer ID from form state
  const selectedCustomerId = customerType === 'business' ? selectedBusinessId : selectedPersonId;

  const selectedCustomer = useMemo(() =>
    customerOptions.find(c => c.id === selectedCustomerId) ?? null,
    [customerOptions, selectedCustomerId]
  );

  // A `defaultCounterparty` preset is a pick made for the user, so it fills
  // the Bill To address the way a user pick does (onChange below) — once,
  // when the customer list has loaded, and only while the preset is still the
  // selected customer with no address (a clone / template that replaced the
  // customer, or an address already typed, wins). Never runs in edit mode
  // (no preset there), so a stored snapshot is never overwritten.
  const presetCustomerId = counterpartyPreset ? counterpartyPreset.business_id || counterpartyPreset.person_id : '';
  const presetAddressAppliedRef = useRef(false);
  useEffect(() => {
    if (!presetCustomerId || presetAddressAppliedRef.current) return;
    const option = customerOptions.find((c) => c.id === presetCustomerId);
    if (!option) return;
    presetAddressAppliedRef.current = true;
    if (selectedCustomerId !== presetCustomerId || form.getValues('customer_address')) return;
    form.setValue('customer_address', option.address ?? '');
  }, [presetCustomerId, customerOptions, selectedCustomerId, form]);

  // Handle payment terms change -> auto-update due date
  const handlePaymentTermsChange = useCallback((termsValue: string) => {
    form.setValue('payment_terms', termsValue);
    const invoiceDateStr = form.getValues('invoice_date');
    const baseDate = parseLocalISODate(invoiceDateStr?.split('T')[0]) ?? new Date();
    const dueDate = calculateDueDate(baseDate, termsValue);
    if (dueDate) {
      form.setValue('due_date', dueDate);
    }
  }, [form]);

  // Handle invoice date change -> re-derive due date from the current terms
  const handleInvoiceDateChange = useCallback((date: Date | undefined) => {
    if (!date) return;
    const dueDate = calculateDueDate(date, form.getValues('payment_terms'));
    if (dueDate) {
      form.setValue('due_date', dueDate);
    }
  }, [form]);

  // Convert businesses to SearchableSelect options format
  const businessOptions = useMemo(() => {
    return businesses?.map(business => ({
      value: business.id,
      label: business.name
    })) || [];
  }, [businesses]);

  // Load existing attachments into state when they're fetched
  useEffect(() => {
    if (existingAttachments && existingAttachments.length > 0) {
      const transformedAttachments = existingAttachments.map((attachment) => ({
        id: attachment.id,
        existingFile: attachment.file,
      }));
      setAttachments(transformedAttachments);
    }
  }, [existingAttachments]);

  // Handle deletion of existing attachments
  const handleDeleteExisting = useCallback(async (attachmentId: string) => {
    if (!invoice?.id || !selectedOrganization?.id) return;
    try {
      await deleteAttachment.mutateAsync({
        organizationId: selectedOrganization.id,
        attachmentId,
        transactionId: invoice.ledger_transaction_id ?? undefined,
        arInvoiceId: invoice.id,
      });
    } catch (error) {
      toast.warning('Failed to delete attachment. Please try again.');
      captureException(error as Error, {
        context: 'AR invoice attachment delete',
        invoiceId: invoice?.id,
      });
    }
  }, [invoice?.id, invoice?.ledger_transaction_id, selectedOrganization?.id, deleteAttachment]);

  // Use a ref to hold the latest submit function to avoid stale closures
  const onSubmitRef = useRef<((data: FormData) => Promise<void>) | undefined>(undefined);

  onSubmitRef.current = async (data: FormData) => {
    if (!selectedOrganization?.id) return;

    const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

    // Get customer name and email based on type
    let customerName: string;
    let customerEmail: string | undefined;
    let businessId: string | undefined;

    if (isIssued && invoice) {
      // An ISSUED invoice's customer is locked (`ISSUED_LOCKED`, the picker is
      // disabled): send back what it was ISSUED to — the stored name and email
      // — never the business / person record as it reads today. A renamed
      // business would otherwise look like a customer change and be refused
      // (the RPC compares the name too), or worse, rewrite the name the
      // customer's copy carries.
      businessId = invoice.business_id ?? undefined;
      customerName = invoice.client_name;
      customerEmail = invoice.client_email ?? undefined;
    } else if (data.customer_type === 'business') {
      if (data.business_id) {
        // Existing business selected
        const selectedBusiness = businesses?.find(b => b.id === data.business_id);
        if (selectedBusiness) {
          businessId = data.business_id;
          customerName = selectedBusiness.name;
          customerEmail = selectedBusiness.contact_email || undefined;
        } else if (data.business_name) {
          // Fallback to custom name if business not found
          customerName = data.business_name.trim();
        } else {
          toast.errorTemplates.generic();
          return;
        }
      } else if (data.business_name) {
        // Custom business name entered
        const trimmedName = data.business_name.trim();
        if (isUUID(trimmedName)) {
          // It's actually a UUID, try to find the business
          const selectedBusiness = businesses?.find(b => b.id === trimmedName);
          if (selectedBusiness) {
            businessId = trimmedName;
            customerName = selectedBusiness.name;
            customerEmail = selectedBusiness.contact_email || undefined;
          } else {
            customerName = trimmedName;
          }
        } else {
          customerName = trimmedName;
        }
      } else {
        toast.errorTemplates.generic();
        return;
      }
    } else {
      const selectedPerson = people?.find(p => p.id === data.person_id);
      if (!selectedPerson) {
        toast.errorTemplates.generic();
        return;
      }
      customerName = `${selectedPerson.first_name} ${selectedPerson.last_name}`;
      customerEmail = selectedPerson.email || undefined;
    }

    const invoiceData = {
      business_id: businessId,
      person_id: data.customer_type === 'person' ? data.person_id : undefined,
      customer_name: customerName,
      customer_email: customerEmail,
      // Always a string, never undefined: on update, an ABSENT address means
      // "leave the stored one alone", so a cleared field has to travel as ''
      // to actually clear it (see the RPC's CASE and the wire schema's note).
      customer_address: data.customer_address ?? '',
      invoice_date: data.invoice_date,
      due_date: data.due_date,
      // '' = the invoice date. An issued invoice sends its stored value back
      // unchanged (the field is locked), so the lock check passes.
      supply_date: data.supply_date || null,
      // ONLY what the user typed. The generated tax notes used to be appended
      // here, but this field is hydrated straight back into `memo` on edit —
      // so every save stacked another copy of the reverse-charge sentence, and
      // deleting the reverse-charge line never removed it. The document now
      // derives the statement from the line items itself
      // (`model.taxStatements`), which is why the box below is a preview.
      description: data.memo ?? '',
      reference: data.reference || '',  // Required param - always pass a string
      ar_account_id: data.ar_account_id,
      project_id: data.project_id === 'none' || data.project_id === '' ? undefined : data.project_id,
      fund_id: data.fund_id === 'none' || data.fund_id === '' ? undefined : data.fund_id,
      line_items: data.line_items.map((item, index) => {
        const { tax_rate, tax_treatment } = resolveTaxTreatment(item.tax_rate, item.is_reverse_charge);
        // Explicit whitelist — form-only fields (is_reverse_charge) must not
        // reach the endpoint; its strict schema rejects unknown keys.
        return {
          revenue_account_id: item.revenue_account_id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate,
          tax_rate_id: item.tax_rate_id || undefined,
          tax_treatment,
          tax_inclusive: false,
          exemption_reason: item.exemption_reason || undefined,
          exemption_reason_other: item.exemption_reason_other || undefined,
          // Rebill link — omitted (never null/'') when the line has none.
          source_expense_id: item.source_expense_id || undefined,
          service_category_id: item.service_category_id || undefined,
          sort_order: index,
        };
      }),
    };

    // "Make this recurring": save a TEMPLATE, not an invoice. The payload is
    // the exact create wire body minus its dates; the shared chain creates (or
    // PATCHes) it, runs it when due today, and navigates to the list. Carbon
    // + attachments are skipped — nothing was created to attach them to.
    if (data.recurring_enabled && data.recurrence) {
      try {
        // A template must never carry a rebill link — every generated invoice
        // would bill the same expense again, and the recurring payload schema
        // rejects the key. buildRecurringPayload passes line keys through
        // untouched, so strip it here explicitly.
        const templateInvoiceData = {
          ...invoiceData,
          line_items: invoiceData.line_items.map(({ source_expense_id: _link, ...line }) => line),
        };
        const payload = buildRecurringPayload('ar_invoice', buildARInvoiceCreateBody(templateInvoiceData));
        await submitRecurringTemplate({
          organizationId: selectedOrganization.id,
          documentType: 'ar_invoice',
          recurrence: data.recurrence,
          payload,
          templateId: recurringTemplate?.id,
          navigateOnSuccess,
        });
        setAttachments([]);
        resetAfterSave();
        onSuccess?.();
      } catch {
        // Toasted + captured by the recurring mutations (MutationCache.onError).
      }
      return;
    }

    try {
      if (isEditMode && invoiceId) {
        await updateARInvoice.mutateAsync({
          organizationId: selectedOrganization!.id,
          invoiceId,
          data: invoiceData,
        });

        // Persist carbon (unified RPC; deletes prior rows by key first). The
        // invoice key always; the journal key too once issued, so legacy rows
        // keyed only by the journal are replaced rather than duplicated.
        if (invoiceId) {
          try {
            await saveCarbonResults(
              { transactionId: invoice?.ledger_transaction_id ?? undefined, arInvoiceId: invoiceId },
              data.line_items,
            );
          } catch (carbonError) {
            captureException(carbonError as Error, {
              context: 'AR invoice carbon emissions update',
              invoiceId,
            });
          }
        }

        // Upload new attachments in edit mode
        const newFiles = attachments.filter(a => a.file).map(a => a.file);
        if (newFiles.length > 0) {
          try {
            await uploadAttachments.mutateAsync({
              organizationId: selectedOrganization!.id,
              // Every new AR attachment keys on the invoice (post-on-issue).
              arInvoiceId: invoiceId,
              files: newFiles,
              attachmentType: 'invoice',
              entityType: 'ar-invoices',
              entityId: invoiceId,
            });
          } catch (attachmentError) {
            toast.warning('Some attachments failed to upload. You can try adding them again.');
            captureException(attachmentError as Error, {
              context: 'AR invoice attachment upload (edit mode)',
              invoiceId,
              fileCount: newFiles.length,
            });
          }
        }
      } else {
        const result = await createARInvoice.mutateAsync({
          organizationId: selectedOrganization!.id,
          data: invoiceData,
        });

        // Persist carbon (unified RPC) against the new DRAFT — it has no
        // journal; Issue graduates these rows onto the posting.
        if (result?.ar_invoice_id) {
          try {
            await saveCarbonResults(
              { arInvoiceId: result.ar_invoice_id },
              data.line_items,
            );
          } catch (carbonError) {
            captureException(carbonError as Error, {
              context: 'AR invoice carbon emissions create',
              invoiceId: result.ar_invoice_id,
            });
          }
        }

        // Upload new attachments (only those with file property, not existing files)
        const newFiles = attachments.filter(a => a.file).map(a => a.file);
        if (result?.ar_invoice_id && newFiles.length > 0) {
          try {
            await uploadAttachments.mutateAsync({
              organizationId: selectedOrganization.id,
              arInvoiceId: result.ar_invoice_id,
              files: newFiles,
              attachmentType: 'invoice',
              entityType: 'ar-invoices',
              entityId: result.ar_invoice_id,
            });
          } catch (attachmentError) {
            toast.warning('Some attachments failed to upload. You can try adding them again.');
            captureException(attachmentError as Error, {
              context: 'AR invoice attachment upload (create mode)',
              invoiceId: result.ar_invoice_id,
              fileCount: newFiles.length,
            });
          }
        }
      }
      setAttachments([]);
      resetAfterSave();
      onSuccess?.();
    } catch (error) {
      // The mutation hook already toasted the translated tax-rate refusal.
      if (isTaxRateRefusal(error)) return;
      if (isEditMode) {
        toast.errorTemplates.update('invoice');
      } else {
        toast.errorTemplates.create('invoice');
      }
      captureException(error as Error, {
        context: isEditMode ? 'AR invoice update' : 'AR invoice create',
        invoiceId: invoiceId || 'new',
      });
    }
  };

  // "Make this recurring" ticked/unticked — the host swaps its footer label.
  const handleRecurringToggle = useCallback((enabled: boolean) => {
    onRecurringChange?.(enabled);
  }, [onRecurringChange]);

  // What the schedule seeds from the moment the box is ticked — read live.
  const getRecurrenceSeed = useCallback(() => {
    const values = form.getValues();
    const customerId = values.customer_type === 'business' ? values.business_id : values.person_id;
    const customer = customerOptions.find((c) => c.id === customerId)?.name;
    const typedName = values.customer_type === 'business' ? values.business_name?.trim() : '';
    return {
      documentDate: values.invoice_date,
      nameBase: customer ?? (typedName || values.memo),
      dueDays: dueDaysFromTerms(values.payment_terms, values.invoice_date, values.due_date),
    };
  }, [form, customerOptions]);

  // Stable wrapper function that calls the ref
  const onSubmit = useCallback(async (data: FormData) => {
    await onSubmitRef.current?.(data);
  }, []);

  // Expose submit handler to parent - only on mount to prevent re-render loops
  // The handler uses refs internally so it always has access to latest state
  useEffect(() => {
    if (onSubmitReady) {
      onSubmitReady(() => form.handleSubmit(onSubmit)());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One mapping for edit AND clone hydration (kept in sync by construction).
  const invoiceToFormData = useCallback((source: ARInvoice, indexArAccountId: string | null) => {
    // The posting is the truth once it exists; a DRAFT books nothing, so its
    // account, fund and reference come from `metadata` (post-on-issue) —
    // COALESCE(<posting>, <metadata>), the same order as `ar_invoice_index`.
    // The AR account of a posting is the index row's `ar_account_id` — the one
    // "which debit is the receivable" rule (`_ar_posting_ar_leg`) — never
    // simply the first debit entry, which can be another leg.
    const meta = (source.metadata ?? {}) as Record<string, unknown>;
    const metaText = (key: string) => (typeof meta[key] === 'string' ? (meta[key] as string) : '');

    // For business_name, use the business_id if it exists (SearchableSelect will show label),
    // otherwise the stored name for a free-text customer. The column is
    // `client_name` (the detail select is `*`); there is no `customer_name`.
    const businessNameValue = source.business_id ||
      (!source.person_id && source.client_name ? source.client_name : '');

    return {
      customer_type: (source.person_id ? 'person' : 'business') as 'business' | 'person',
      business_id: source.business_id || '',
      business_name: businessNameValue,
      person_id: source.person_id || '',
      // Both columns are plain `date` values, so the server already hands us
      // the calendar day. Slice rather than round-tripping through a JS Date
      // (`new Date('2026-03-15')` is UTC midnight and formats as the 14th
      // for any UTC− browser); the slice also absorbs any legacy timestamptz
      // string still cached from before the column conversion.
      invoice_date: source.invoice_date.slice(0, 10),
      due_date: source.due_date.slice(0, 10),
      supply_date: metaText('supply_date').slice(0, 10),
      payment_terms: 'custom' as string,
      memo: source.description || '',
      reference: source.ledger_transaction?.reference || metaText('reference'),
      ar_account_id: indexArAccountId || metaText('ar_account_id'),
      project_id: source.project_id || '',
      fund_id: source.ledger_transaction?.fund_id || metaText('fund_id'),
      // The snapshot as issued, NOT the customer's address today: reopening
      // an invoice must show what it prints. Empty when it was never
      // snapshotted (every invoice from before the address field existed) —
      // and empty is exactly what makes the document keep resolving live.
      customer_address: source.client_address || '',
      line_items: source.line_items?.map(item => {
        const { taxRate, isReverseCharge } = toSalesTaxRateType(item.tax_treatment, item.tax_rate ?? 0);
        return {
          revenue_account_id: item.revenue_account_id || '',
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: taxRate as SalesTaxRateType,
          tax_rate_id: item.tax_rate_id || '',
          tax_inclusive: false,
          is_reverse_charge: isReverseCharge,
          exemption_reason: item.exemption_reason || '',
          exemption_reason_other: item.exemption_reason_other || '',
          // Edits keep the rebill link (the clone effect drops it).
          source_expense_id: item.source_expense_id || undefined,
          service_category_id: item.service_category_id || undefined,
        };
      }) || [],
      // Advanced VAT Options
      self_billing: source.self_billing ?? false,
      additional_vat_notes: source.additional_vat_notes || '',
    };
  }, []);

  // Load existing invoice data when in edit mode
  useEffect(() => {
    // Wait for all data to be loaded before populating the form
    if (isEditMode && invoice && accounts && projects && funds && businesses && people && !isLoadingData) {
      const formData = invoiceToFormData(invoice, sourceIndexArAccountId);
      // Use a timeout to ensure the DOM has updated with the Select options
      setTimeout(() => {
        form.reset(formData);
        setIsFormReady(true);
      }, 0);
    }
  }, [isEditMode, invoice, accounts, projects, funds, businesses, people, isLoadingData, invoiceToFormData, form, sourceIndexArAccountId]);

  // Clone mode: same mapping, saved as NEW. Dates move to today (the due date
  // keeps its distance), the number is left for the RPC to assign, and the
  // recurring fields from `defaultValues` are preserved. `reset` makes these
  // the new defaults, so a freshly cloned form is not dirty.
  const clonedFromIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEditMode || !cloneFrom || !accounts || !projects || !funds || !businesses || !people || isLoadingData) return;
    if (clonedFromIdRef.current === cloneFrom.id) return;
    clonedFromIdRef.current = cloneFrom.id;
    const mapped = invoiceToFormData(cloneFrom, sourceIndexArAccountId);
    // A copy bills nothing: the rebill link belongs to the source invoice.
    const clonedLines = mapped.line_items.map(({ source_expense_id: _link, ...line }) => line);
    const today = format(new Date(), 'yyyy-MM-dd');
    // The source's payment distance in calendar days (DST-safe), re-applied from today.
    const termDays = dueDaysFromTerms('custom', cloneFrom.invoice_date.slice(0, 10), cloneFrom.due_date.slice(0, 10)) ?? 30;
    const current = form.getValues();
    form.reset({
      ...current,
      ...mapped,
      line_items: clonedLines,
      supply_date: '', // the copy supplies on its own date
      reference: '', // a ledger reference (cheque/PO no.) belongs to the source, not the copy
      invoice_date: today,
      due_date: formatLocalISODate(addDays(new Date(), termDays)) ?? today,
      payment_terms: 'custom',
      recurring_enabled: current.recurring_enabled,
      recurrence: current.recurrence,
    });
    setIsFormReady(true);
    // "Make recurring" from a row: the box starts ticked with an EMPTY name
    // (the New-menu flow leaves it for the user) — seed it from the cloned
    // payee now that the form holds the cloned values. Still a reset, not a
    // setValue, so the form stays pristine.
    const afterClone = form.getValues();
    if (afterClone.recurring_enabled && afterClone.recurrence && !afterClone.recurrence.name) {
      const seed = getRecurrenceSeed();
      form.reset({
        ...afterClone,
        recurrence: {
          ...afterClone.recurrence,
          name: suggestTemplateName(seed.nameBase, afterClone.recurrence.frequency, (key, vars) => String(t(key, vars))),
        },
      });
    }
  }, [isEditMode, cloneFrom, accounts, projects, funds, businesses, people, isLoadingData, invoiceToFormData, form, getRecurrenceSeed, t, sourceIndexArAccountId]);

  // Filter accounts by category — Revenue, plus any account a loaded line
  // already posts to (a fund installment's "on receipt" line credits Deferred
  // Grant Revenue, a liability; without it the read-only line shows the
  // placeholder instead of the account it actually posts to).
  const lineAccountKey = carbonLineItemAccountIds.filter(Boolean).join(',');
  const revenueAccounts = useMemo(() => {
    const onLines = new Set(lineAccountKey ? lineAccountKey.split(',') : []);
    return accounts?.filter(acc => acc.category?.category_name === 'Revenue' || onLines.has(acc.id)) || [];
  }, [accounts, lineAccountKey]);

  const assetAccounts = useMemo(() => {
    return accounts?.filter(acc =>
      acc.category?.category_name === 'Assets' &&
      (acc.type?.type_name === 'Accounts Receivable' || acc.account_name.toLowerCase().includes('receivable'))
    ) || [];
  }, [accounts]);

  // Auto-select AR account (only for new invoices)
  useEffect(() => {
    if (isEditMode) return;
    if (!assetAccounts.length || form.getValues('ar_account_id')) return;

    // Priority 1: System AR account
    const systemArAccount = assetAccounts.find(acc =>
      acc.system_account_type === 'accounts_receivable'
    );
    if (systemArAccount) {
      form.setValue('ar_account_id', systemArAccount.id);
      return;
    }

    // Priority 2: Type match + currency match
    const defaultCurrency = selectedOrganization?.currency;
    if (defaultCurrency) {
      const currencyMatch = assetAccounts.find(acc =>
        acc.type?.type_name === 'Accounts Receivable' &&
        (acc.currency === defaultCurrency || (!acc.currency && defaultCurrency))
      );
      if (currencyMatch) {
        form.setValue('ar_account_id', currencyMatch.id);
        return;
      }
    }
  }, [assetAccounts, selectedOrganization?.currency, form, isEditMode]);


  // Get the currency from the selected AR account
  const selectedArAccountCurrency = useMemo(() => {
    if (!selectedArAccountId || !assetAccounts.length) return null;
    const selectedAccount = assetAccounts.find(acc => acc.id === selectedArAccountId);
    return selectedAccount?.currency || null;
  }, [selectedArAccountId, assetAccounts]);

  // Auto-select AR account when only one exists (only in create mode)
  useEffect(() => {
    if (!isEditMode && assetAccounts.length === 1 && !form.getValues('ar_account_id')) {
      form.setValue('ar_account_id', assetAccounts[0].id);
    }
  }, [assetAccounts, isEditMode, form]);

  // Show loading state while data is loading or form is being populated in edit mode
  if (isLoadingData || (isEditMode && !isFormReady)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-2">
          <div className="text-muted-foreground">{t('billing.form.loadingInvoice')}</div>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <div className="space-y-8">
        {readOnlyReason?.kind === 'declared' && (
          <DeclaredVatBanner
            organizationId={selectedOrganization?.id}
            settlementId={readOnlyReason.settlementId}
            messageKey="tax.declared.readOnlyBanner"
          />
        )}
        {readOnlyReason?.kind === 'filed_period' && (
          <DeclaredVatBanner
            organizationId={selectedOrganization?.id}
            filedOnDate={readOnlyReason.invoiceDate}
            messageKey="invoices.lifecycle.readOnly.filedPeriod"
          />
        )}
        {(readOnlyReason?.kind === 'cancelled' ||
          readOnlyReason?.kind === 'legacy_posted' ||
          readOnlyReason?.kind === 'paid') && (
          <Alert variant="info" data-testid="ar-read-only-banner">
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{t(`invoices.lifecycle.readOnly.${readOnlyReason.kind}`)}</AlertDescription>
          </Alert>
        )}
        {isIssued && !isReadOnly && (
          <Alert variant="warning" data-testid="ar-issued-edit-warning">
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{t('invoices.lifecycle.issuedEditWarning')}</AlertDescription>
          </Alert>
        )}
        {readOnlyReason?.kind === 'fund' && (
          <Alert variant="info" data-testid="fund-managed-banner">
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              {readOnlyFundCode
                ? t('invoices.managed.fundReadOnlyBanner', { code: readOnlyFundCode })
                : t('invoices.managed.fundDescription')}
              {readOnlyFundCode && (
                <>
                  {' '}
                  <Link
                    to={`${orgBasePath}${fundDetailPath(readOnlyFundCode)}`}
                    className="font-medium underline underline-offset-4"
                  >
                    {t('invoices.managed.openFund')}
                  </Link>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {showChangeFundHint && (
          <Alert variant="info" data-testid="ar-change-fund-hint">
            <Info className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{t('invoices.lifecycle.readOnly.changeFundHint')}</AlertDescription>
          </Alert>
        )}

        {/* Project, Fund, and AR Account Selection Row — outside the read-only
            fieldset below; each picker carries its own `disabled` (the AR
            account also locks on issue). */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="project_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('billing.form.projectOptional')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                <Select onValueChange={field.onChange} value={field.value} disabled={isReadOnly}>
                  <FormControl>
                    <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                      <SelectValue placeholder={t('billing.form.selectProject')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">{t('billing.form.noProject')}</SelectItem>
                    {projects?.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fund_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('billing.form.fundOptional')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p className="text-sm">{t('billing.form.fundTooltip')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <FundSelect
                  formControl
                  funds={funds ?? []}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder={t('billing.form.selectFund')}
                  specialOptions={[{ value: 'none', label: t('billing.form.noFund') }]}
                  className={fieldState.error ? 'border-destructive' : undefined}
                  // Never locked by issue (Clay 2026-11-02, fund is not a VAT
                  // dimension): the issued edit re-posts with the new fund.
                  // A read-only invoice's fund moves through the panel's
                  // "Change fund" action instead (`showChangeFundHint`).
                  disabled={isReadOnly}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ar_account_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{t('invoices.form.arAccount')}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                {/* An issued invoice's AR control account is locked (`ISSUED_LOCKED`). */}
                <Select onValueChange={field.onChange} value={field.value} disabled={isIssued || isReadOnly}>
                  <FormControl>
                    <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                      <SelectValue placeholder={t('invoices.payment.selectArAccount')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {assetAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {getSystemAccountDisplayName(account, t)} - {account.currency || selectedOrganization?.currency || 'EUR'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        {/* Read-only: ONE native `disabled` fieldset disables every input,
            trigger and button below (`contents` keeps it out of the layout). */}
        <fieldset disabled={isReadOnly} className="contents">

        {/* Invoice Content Card - wraps FROM, Bill To, and Line Items */}
        <Card>
          <CardContent className="pt-6 space-y-8">
            {/* SECTION: FROM (Organization Info) */}
            <div>
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">{t('invoices.form.invoiceTitle')}</h2>
              <div className="flex items-start justify-between">
                {/* Left side: org info */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                    {t('invoices.form.from')}
                  </h3>
                  <p className="font-semibold text-lg">{selectedOrganization?.name}</p>
                  <OrganizationIdentityDisplay
                    orgIdentity={orgIdentity}
                    vatNumber={taxSettings?.tax_identification_number}
                    orgCountry={selectedOrganization?.country}
                    taxJurisdiction={taxSettings?.primary_tax_jurisdiction}
                    showBanking={true} // Show IBAN so customer knows where to pay
                  />
                </div>

                {/* Right side: logo (aligned with org name) */}
                {selectedOrganization?.logo_small && (
                  <EntityImage
                    imagePath={selectedOrganization.logo_small}
                    alt={selectedOrganization.name}
                    className="h-24 w-24 mt-5"
                    variant="square"
                  />
                )}
              </div>
            </div>

            {/* Full-bleed colored section: Bill To + Invoice Details */}
            <div className="-mx-6 px-6 py-6 bg-releve-gray-light">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: Bill To */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700">
                      {t('invoices.form.billTo')}
                    </h3>
                    {form.formState.errors.customer_type && (
                      <span className="text-xs text-destructive bg-white/90 px-1 rounded">{form.formState.errors.customer_type.message}</span>
                    )}
                  </div>
                  <CustomerSelect
                    customers={customerOptions}
                    value={selectedCustomerId}
                    customerType={customerType}
                    onChange={(id, type) => {
                      form.setValue('customer_type', type);
                      if (type === 'business') {
                        form.setValue('business_id', id);
                        form.setValue('person_id', '');
                      } else {
                        form.setValue('person_id', id);
                        form.setValue('business_id', '');
                      }
                      // Refill the address from whoever was just picked. Only
                      // on a USER pick, never in an effect: edit mode hydrates
                      // the stored snapshot, and an effect keyed on the
                      // customer id would overwrite it on load.
                      form.setValue('customer_address', customerOptions.find(c => c.id === id)?.address ?? '');
                    }}
                    onClear={() => {
                      form.setValue('business_id', '');
                      form.setValue('person_id', '');
                      form.setValue('customer_address', '');
                    }}
                    hasError={!!form.formState.errors.customer_type}
                    // Locked once issued (`ISSUED_LOCKED`).
                    disabled={isIssued}
                  />
                  {/* Selected customer: name and email are read-only facts of
                      the customer record; the ADDRESS is editable, because it
                      is what this invoice prints. */}
                  {selectedCustomer && (
                    <div className="mt-3 p-3 bg-white/60 rounded-md">
                      <div className="font-medium text-sm text-gray-800">{selectedCustomer.name}</div>
                      {selectedCustomer.email && (
                        <div className="text-sm text-gray-600 mt-1">
                          {selectedCustomer.email}
                        </div>
                      )}
                      <FormField
                        control={form.control}
                        name="customer_address"
                        render={({ field }) => (
                          <FormItem className="mt-2 space-y-1">
                            <FormLabel className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                              {t('invoices.form.billingAddress')}
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={t('invoices.form.billingAddressPlaceholder')}
                                className="bg-white text-sm text-gray-600"
                                rows={3}
                                {...field}
                              />
                            </FormControl>
                            <p className="text-[11px] text-gray-500">
                              {t('invoices.form.billingAddressHint')}
                            </p>
                          </FormItem>
                        )}
                      />
                      {customerType === 'business' && customerVendorIdentity && (
                        <VendorIdentityDisplay vendorIdentity={customerVendorIdentity} />
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column: Invoice Details */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-3">
                    {t('invoices.form.invoiceDetails')}
                  </h3>
                  <div className="space-y-4">
                    {/* Row 1: Invoice Number (editable in create, read-only in edit), Invoice Date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* The number is assigned at Issue (post-on-issue): a
                          draft shows "Draft". Display only — never typed. */}
                      <div className="space-y-2">
                        <span className="text-sm font-extralight text-gray-700">{t('invoices.form.invoiceNumber')}</span>
                        <div
                          className="flex h-10 items-center rounded-md border border-input bg-white px-3 text-sm text-muted-foreground"
                          data-testid="ar-invoice-number"
                        >
                          {arInvoiceNumberLabel(t, isEditMode ? invoice?.invoice_number : null)}
                        </div>
                      </div>

                      <FormField
                        control={form.control}
                        name="invoice_date"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.invoiceDate')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DatePicker
                                value={parseLocalISODate(field.value?.split('T')[0])}
                                onChange={(date) => {
                                  field.onChange(formatLocalISODate(date) || '');
                                  handleInvoiceDateChange(date);
                                }}
                                // Locked once issued (`ISSUED_LOCKED`) — Issue set it.
                                disabled={recurringEnabled || isIssued}
                                className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              />
                            </FormControl>
                            {recurringEnabled && (
                              // Frozen paper band → pinned hint colour (FULLSCREEN_FORM_MODALS.md).
                              <p className="text-xs text-gray-500 mt-1">{t('recurring.form.setBySchedule')}</p>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Row 2: Payment Terms, Due Date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="payment_terms"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.paymentTerms')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select
                              onValueChange={handlePaymentTermsChange}
                              value={field.value}
                              disabled={recurringEnabled}
                            >
                              <FormControl>
                                <SelectTrigger className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}>
                                  <SelectValue placeholder={t('billing.form.selectTerms')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {PAYMENT_TERMS_OPTIONS.map((term) => {
                                  // Map payment term values to translation keys
                                  const keyMap: Record<string, string> = {
                                    'due_on_receipt': 'dueOnReceipt',
                                    'net_7': 'net7',
                                    'net_15': 'net15',
                                    'net_30': 'net30',
                                    'net_45': 'net45',
                                    'net_60': 'net60',
                                    'net_90': 'net90',
                                    'custom': 'custom',
                                  };
                                  const translationKey = keyMap[term.value] || term.value;
                                  return (
                                    <SelectItem key={term.value} value={term.value}>
                                      {t(`invoices.paymentTerms.${translationKey}`)}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="due_date"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.dueDate')}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DatePicker
                                value={parseLocalISODate(field.value)}
                                onChange={(date) => {
                                  field.onChange(formatLocalISODate(date) || '');
                                  // Manual due-date edits switch terms to Custom so the
                                  // value isn't recalculated away by later date changes
                                  form.setValue('payment_terms', 'custom');
                                }}
                                disabled={recurringEnabled}
                                className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              />
                            </FormControl>
                            {recurringEnabled && (
                              <p className="text-xs text-gray-500 mt-1">{t('recurring.form.setBySchedule')}</p>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Row 3: Reference, Supply date */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="reference"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.reference')}</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive bg-white/90 px-1 rounded">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Input
                              placeholder={t('invoices.form.referencePlaceholder')}
                              className={`bg-white ${fieldState.error ? "border-destructive" : ""}`}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {/* The supply date — printed on the invoice when it differs
                        from the invoice date. A template has none (each
                        occurrence supplies on its own date). */}
                    {!recurringEnabled && !isTemplateMode && (
                      <FormField
                        control={form.control}
                        name="supply_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-700 font-extralight">{t('invoices.form.supplyDate')}</FormLabel>
                            <FormControl>
                              <DatePicker
                                value={parseLocalISODate(field.value || undefined)}
                                onChange={(date) => field.onChange(formatLocalISODate(date) || '')}
                                // Locked once issued (`ISSUED_LOCKED`).
                                disabled={isIssued}
                                className="bg-white"
                              />
                            </FormControl>
                            <p className="text-xs text-gray-500 mt-1">{t('invoices.form.supplyDateHint')}</p>
                          </FormItem>
                        )}
                      />
                    )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION: LINE ITEMS */}
            <InvoiceLineItems
              asOfDate={form.watch('invoice_date')}
              type="ar"
              form={form}
              revenueAccounts={revenueAccounts}
              currency={selectedArAccountCurrency}
              carbonDataByLineItem={carbonDataByLineItem}
              onCarbonDataChange={updateCarbonData}
              onTaxNotesChange={(notes) => {
                taxNotesRef.current = notes;
                setHasTaxNotes(!!notes);
              }}
              summaryLeftContent={
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="memo"
                    render={({ field, fieldState }) => (
                      <FormItem className="space-y-2">
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t('invoices.form.noteToCustomer')}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t('invoices.form.noteToCustomerPlaceholder')}
                            className={fieldState.error ? "border-destructive" : ""}
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        {fieldState.error && (
                          <span className="text-xs text-destructive">{fieldState.error.message}</span>
                        )}
                      </FormItem>
                    )}
                  />
                  {hasTaxNotes && (
                    <Alert className="bg-blue-50 border-blue-200">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-xs text-blue-700 whitespace-pre-line">
                        {taxNotesRef.current}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              }
            />
          </CardContent>
        </Card>

        {/* SECTION: ADVANCED VAT OPTIONS */}
        <Collapsible open={vatOptionsOpen || isReadOnly} onOpenChange={setVatOptionsOpen} className="border rounded-lg">
          <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 hover:bg-muted/50 transition-colors">
            <ChevronRight className={`h-4 w-4 transition-transform ${vatOptionsOpen ? 'rotate-90' : ''}`} />
            <span className="text-sm font-medium">{t('vat.advancedOptions.toggle')}</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4 space-y-4">
            <FormField
              control={form.control}
              name="self_billing"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">{t('vat.advancedOptions.selfBilling.label')}</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      {t('vat.advancedOptions.selfBilling.hint')}
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="additional_vat_notes"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className="text-sm">{t('vat.advancedOptions.additionalNotes.label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('vat.advancedOptions.additionalNotes.placeholder')}
                      className={fieldState.error ? "border-destructive" : ""}
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CollapsibleContent>
        </Collapsible>

        {/* SECTION: MAKE THIS RECURRING — sits above the attachments so the
            upload zone can be disabled while ticked (attachments are not copied
            to generated documents). Never in document-edit mode. */}
        {(allowRecurring || isTemplateMode) && !isEditMode && (
          <RecurringSection
            documentType="ar_invoice"
            orgToday={orgToday}
            getSeed={getRecurrenceSeed}
            template={recurringTemplate}
            onEnabledChange={handleRecurringToggle}
          />
        )}

        {/* SECTION: ATTACHMENTS */}
        <div className="w-1/2 max-w-md ml-auto space-y-2">
          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('invoices.form.attachments')}
          </FormLabel>
          {recurringEnabled && (
            <p className="text-xs text-muted-foreground">{t('recurring.form.notes.notCopied')}</p>
          )}
          <Card>
            <CardContent className="p-3">
              <FileAttachments
                disabled={recurringEnabled || isReadOnly}
                value={attachments}
                onChange={setAttachments}
                onDeleteExisting={handleDeleteExisting}
                maxFiles={10}
                acceptedTypes={['image/*', 'application/pdf', '.doc', '.docx', '.xls', '.xlsx']}
                className="h-full"
              />
            </CardContent>
          </Card>
        </div>

        {isTemplateMode && onRequestDelete && (
          <DangerZone
            title={t('recurring.dangerZone.heading')}
            actionTitle={t('recurring.dangerZone.title')}
            description={t('recurring.dangerZone.description')}
            buttonLabel={t('recurring.dangerZone.action')}
            onAction={onRequestDelete}
          />
        )}
        </fieldset>
      </div>
    </Form>
  );
};
